import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SectionWrapper from '../shared/SectionWrapper';
import { FloatingOrbs, FloatingShapes, FloatingParticles } from '../shared/FloatingElements';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const comparisonData = [
  {
    feature: 'Data Collection',
    manual: '100+ hrs/cycle',
    generic: '40+ hrs/cycle',
    esg: '< 2 hrs/cycle',
  },
  {
    feature: 'Report Generation',
    manual: '2-4 weeks',
    generic: '3-5 days',
    esg: '60 seconds',
  },
  {
    feature: 'Framework Coverage',
    manual: '1-2 frameworks',
    generic: '5-10 frameworks',
    esg: '35+ frameworks',
  },
  {
    feature: 'AI Analysis',
    manual: null,
    generic: 'Basic',
    esg: 'Brew AI',
  },
  {
    feature: 'Audit Trail',
    manual: null,
    generic: 'Partial',
    esg: 'Complete',
  },
  {
    feature: 'Real-time Updates',
    manual: null,
    generic: null,
    esg: 'Yes',
  },
  {
    feature: 'Carbon Accounting',
    manual: null,
    generic: 'Basic',
    esg: 'Scope 1-3',
  },
  {
    feature: 'Cost',
    manual: '$200K+/year',
    generic: '$50-100K/year',
    esg: '$10-70k/year',
  },
];

const CellValue = ({ value, isEsg = false }) => {
  if (value === null) {
    return <X className="w-5 h-5 text-red-400 mx-auto" />;
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {isEsg && (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-3 h-3 text-green-600" />
        </span>
      )}
      <span className={isEsg ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-500 dark:text-slate-400'}>
        {value}
      </span>
    </div>
  );
};

const MobileComparisonCard = ({ row, index }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    custom={index}
    className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-4 shadow-sm"
  >
    <div className="text-sm font-bold text-gray-900 dark:text-white mb-3">{row.feature}</div>
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400 dark:text-slate-500 w-24">Manual</span>
        <div className="text-sm">
          {row.manual === null ? (
            <X className="w-4 h-4 text-red-400" />
          ) : (
            <span className="text-gray-500 dark:text-slate-400">{row.manual}</span>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400 dark:text-slate-500 w-24">Generic Tools</span>
        <div className="text-sm">
          {row.generic === null ? (
            <X className="w-4 h-4 text-red-400" />
          ) : (
            <span className="text-gray-500 dark:text-slate-400">{row.generic}</span>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/30 -mx-2 px-2 py-1.5 rounded-lg">
        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 w-24">ESGCaffe</span>
        <div className="flex items-center gap-1.5 text-sm">
          <span className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-green-600" />
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">{row.esg}</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const ComparisonSection = () => {
  const [mobileView, setMobileView] = useState('all'); // 'all' | 'esgcaffe'

  return (
    <SectionWrapper id="comparison" bgClass="bg-white dark:bg-[#0f172a]" padding="py-10 md:py-12" className="relative">
      {/* Floating background decorations */}
      <FloatingOrbs
        orbs={[
          { size: 240, color: 'rgba(59,130,246,0.05)', top: '8%', left: '85%', drift: 20, duration: 10, delay: 0 },
          { size: 200, color: 'rgba(16,185,129,0.04)', top: '65%', left: '-3%', drift: 16, duration: 11, delay: 2 },
        ]}
      />
      <FloatingShapes
        shapes={[
          { type: 'ring', size: 14, color: '#3b82f6', top: '10%', left: '4%', drift: 18, delay: 0, duration: 7, opacity: 0.08 },
          { type: 'dot', size: 6, color: '#10b981', top: '85%', left: '95%', drift: 14, delay: 1, duration: 6, opacity: 0.1 },
          { type: 'diamond', size: 8, color: '#f59e0b', top: '50%', left: '97%', drift: 16, delay: 2, duration: 8, opacity: 0.08 },
        ]}
      />
      <FloatingParticles count={16} color="#3b82f6" theme="light" />

      {/* Header */}
      <div className="text-center mb-14">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-gray-900 dark:text-white tracking-tight mb-5"
        >
          Why Leading Companies Choose <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">ESGCaffe</span>
        </motion.h2>
      </div>

      {/* Mobile Toggle */}
      <div className="md:hidden flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-slate-800 rounded-full p-1">
          <button
            onClick={() => setMobileView('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              mobileView === 'all'
                ? 'bg-white dark:bg-slate-700 shadow-sm text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-slate-400'
            }`}
          >
            Full Comparison
          </button>
          <button
            onClick={() => setMobileView('esgcaffe')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              mobileView === 'esgcaffe'
                ? 'bg-white dark:bg-slate-700 shadow-sm text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-slate-400'
            }`}
          >
            ESGCaffe Only
          </button>
        </div>
      </div>

      {/* Mobile View - Cards */}
      {mobileView === 'all' ? (
        <div className="md:hidden space-y-3">
          {comparisonData.map((row, i) => (
            <MobileComparisonCard key={row.feature} row={row} index={i} />
          ))}
        </div>
      ) : (
        <div className="md:hidden space-y-3">
          {comparisonData.map((row, i) => (
            <motion.div
              key={row.feature}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i}
              className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-blue-800/50 p-4 shadow-sm"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{row.feature}</span>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-600" />
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{row.esg}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Desktop View - Card Columns */}
      <div className="hidden md:grid md:grid-cols-3 gap-5">
        {/* Column 1: Manual Process */}
        <Link to="/comparison" className="block cursor-pointer">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden h-full hover:shadow-lg hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-300"
          >
            <div className="bg-gray-100 dark:bg-slate-700 px-6 py-5 text-center">
              <h3 className="text-lg font-bold text-gray-700 dark:text-slate-300">Manual Process</h3>
              <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">Spreadsheets & consultants</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {comparisonData.map((row, i) => (
                <motion.div
                  key={row.feature}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.5}
                  className="px-6 py-4 text-center"
                >
                  <div className="text-xs text-gray-400 dark:text-slate-500 mb-1 font-medium">{row.feature}</div>
                  <CellValue value={row.manual} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Link>

        {/* Column 2: Generic Tools */}
        <Link to="/comparison" className="block cursor-pointer">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden h-full hover:shadow-lg hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-300"
          >
            <div className="bg-gray-100 dark:bg-slate-700 px-6 py-5 text-center">
              <h3 className="text-lg font-bold text-gray-700 dark:text-slate-300">Generic Tools</h3>
              <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">One-size-fits-all platforms</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {comparisonData.map((row, i) => (
                <motion.div
                  key={row.feature}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.5}
                  className="px-6 py-4 text-center"
                >
                  <div className="text-xs text-gray-400 dark:text-slate-500 mb-1 font-medium">{row.feature}</div>
                  <CellValue value={row.generic} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Link>

        {/* Column 3: ESGCaffe (Recommended) */}
        <Link to="/comparison" className="block cursor-pointer">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            className="rounded-2xl border-2 border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 overflow-hidden ring-2 ring-blue-500/20 shadow-xl h-full hover:shadow-2xl hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300"
          >
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-5 text-center rounded-t-2xl">
              <h3 className="text-lg font-bold text-white mt-1">ESGCaffe</h3>
              <p className="text-sm text-blue-100 mt-1">AI-powered ESG platform</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {comparisonData.map((row, i) => (
                <motion.div
                  key={row.feature}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.5}
                  className="px-6 py-4 text-center bg-blue-50/30 dark:bg-blue-900/10"
                >
                  <div className="text-xs text-gray-400 dark:text-slate-500 mb-1 font-medium">{row.feature}</div>
                  <CellValue value={row.esg} isEsg />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Link>
      </div>

      {/* CTA */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={6}
        className="text-center mt-14"
      >
        <p className="text-gray-500 dark:text-slate-400 mb-5 text-lg">
          See why <span className="font-semibold text-gray-700 dark:text-slate-300">15+ companies</span> made the switch
        </p>
        <a
          href="#demo"
          className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3.5 rounded-md shadow-sm transition-all duration-300"
        >
          Book a Demo
          <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </SectionWrapper>
  );
};

export default ComparisonSection;
